import clsx from 'clsx';
import { RotateCcw } from 'lucide-react';
import { ZOOM_LEVELS, type ZoomLevel } from '@arcanum-hq/shared-types';

interface SheetTabsProps {
  sheets: string[];
  activeSheet: string;
  onSheetSelect: (sheet: string) => void;
  /**
   * Per-sheet "has column-width overrides" predicate (or membership set).
   * Drives the per-tab `RotateCcw` reset icon — only renders when the
   * sheet has at least one customized width to reset. Undefined → all
   * tabs render without the reset affordance (the Phase 3.4 default-off
   * behavior pre-drag).
   * (model-view-ux-polish Phase 3 Session 3.4, 2026-04-27)
   */
  sheetsWithOverrides?: ReadonlySet<string>;
  /**
   * Callback fired when the user clicks the reset icon on a sheet tab.
   * Optional — without it, the icon doesn't render even when the sheet is
   * in `sheetsWithOverrides` (the affordance is meaningless without a
   * handler).
   */
  onResetSheetWidths?: (sheet: string) => void;
  /**
   * Current zoom level (model-view-excel-parity Phase 2 S2, ADR-0012).
   * Renders a native `<input type="range">` slider (right-aligned) that
   * snaps to the 6 discrete ZOOM_LEVELS. Omit to hide the zoom control
   * (e.g. public-viewer embeds where zoom state is not wired).
   */
  zoom?: ZoomLevel;
  /**
   * Callback fired when the user changes the zoom slider.
   * Required when `zoom` is provided.
   */
  onZoomChange?: (next: ZoomLevel) => void;
}

/**
 * Bottom row of sheet-tabs below the grid, sourced from
 * `layout.visibleSheetNames`. Active tab gets the accent bar + accent text;
 * inactive tabs are muted. Click switches the active sheet via the parent's
 * state setter (lifted in `<ModelLayout>`).
 *
 * Session 1 ships this in its full form because the visual contract (active
 * accent bar, mono labels, panel-bg base) is stable across phases.
 *
 * Session 3.4 (model-view-ux-polish, 2026-04-27): per-sheet `RotateCcw`
 * reset icon appears (on hover, opacity-0 → opacity-100) only when the
 * sheet appears in `sheetsWithOverrides`. Click clears that sheet's
 * `colWidths` entry via `onResetSheetWidths`. Hidden by default to keep
 * the tab strip clean for sheets the user hasn't customized.
 */
export function SheetTabs({
  sheets,
  activeSheet,
  onSheetSelect,
  sheetsWithOverrides,
  onResetSheetWidths,
  zoom,
  onZoomChange,
}: SheetTabsProps) {
  if (sheets.length === 0) return null;

  const handleZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onZoomChange) return;
    const raw = Number(e.target.value);
    // Snap to nearest ZOOM_LEVELS entry. step=25 gives 7 stops (50–200);
    // the nearest-snap collapses the 175 ghost stop to 150 or 200.
    const nearest = ZOOM_LEVELS.reduce<ZoomLevel>(
      (best, level) =>
        Math.abs(level - raw) < Math.abs(best - raw) ? level : best,
      ZOOM_LEVELS[0]
    );
    onZoomChange(nearest);
  };

  return (
    <div className="flex items-stretch border-t border-panel-border bg-panel-bg overflow-x-auto">
      {sheets.map((s) => {
        const showReset =
          !!onResetSheetWidths && (sheetsWithOverrides?.has(s) ?? false);
        return (
          <div
            key={s}
            className={clsx(
              'group flex items-stretch border-r border-panel-border',
              s === activeSheet
                ? 'border-t-2 border-t-accent bg-bg'
                : 'border-t-2 border-t-transparent bg-transparent'
            )}
          >
            <button
              type="button"
              onClick={() => onSheetSelect(s)}
              data-testid={`sheet-tab-${s}`}
              className={clsx(
                'px-3 py-1.5 text-xs font-mono cursor-pointer transition-colors whitespace-nowrap bg-transparent border-none',
                s === activeSheet
                  ? 'text-accent'
                  : 'text-text-muted hover:text-text'
              )}
            >
              {s}
            </button>
            {showReset && (
              <button
                type="button"
                onClick={() => onResetSheetWidths?.(s)}
                className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity flex items-center justify-center px-1.5 text-text-dim hover:text-text-muted cursor-pointer bg-transparent border-none"
                aria-label={`Reset column widths for ${s}`}
                title={`Reset column widths for ${s}`}
                data-testid={`sheet-reset-widths-${s}`}
              >
                <RotateCcw size={12} />
              </button>
            )}
          </div>
        );
      })}

      {/* Spacer — pushes zoom slider to right edge */}
      <div className="flex-1" />

      {/* Phase 2 S2 — inline native range, accent-tinted per design-system
          convention for native form controls (apps/web CLAUDE.md "Native
          form-control with token tint when no custom primitive exists").
          Only rendered when zoom + onZoomChange are provided (authenticated
          builder path). Public viewer omits both props → control hidden. */}
      {zoom !== undefined && onZoomChange && (
        <div
          className="flex items-center gap-2 px-3 shrink-0"
          data-testid="zoom-slider-container"
        >
          <label
            htmlFor="zoom-slider"
            className="text-text-muted text-[10px] font-mono select-none"
          >
            ZOOM
          </label>
          <input
            id="zoom-slider"
            type="range"
            min={50}
            max={200}
            step={25}
            value={zoom}
            onChange={handleZoomChange}
            className="accent-accent w-24"
            data-testid="zoom-slider-input"
            aria-label={`Zoom ${zoom}%`}
          />
          <span className="text-text-bright text-[10px] font-mono tabular-nums w-10 text-right select-none">
            {zoom}%
          </span>
        </div>
      )}
    </div>
  );
}
